/**
 * Role utilities for authentication and authorization
 */

/**
 * User roles in the system
 */
export enum Roles {
  UPERCUT = 'UPERCUT',
  PRO = 'PRO',
  COMPANY = 'COMPANY',
  FREELANCE = 'FREELANCE',
  TALENT = 'TALENT',
  ADMIN_PRO = 'ADMIN_PRO',
  MEMBER_PRO = 'MEMBER_PRO',
  ACCOUNTANT_PRO = 'ACCOUNTANT_PRO',
}

/**
 * Check if a role is a Pro user type
 * Pro users include: PRO, COMPANY, FREELANCE
 */
export function isProRole(role: string | Roles): boolean {
  return [Roles.PRO, Roles.COMPANY, Roles.FREELANCE].includes(role as Roles);
}

/**
 * Check if a role is an Upercut user
 */
export function isUpercutRole(role: string | Roles): boolean {
  return role === Roles.UPERCUT;
}

/**
 * Check if a role is a Talent user
 */
export function isTalentRole(role: string | Roles): boolean {
  return role === Roles.TALENT;
}

/**
 * Check if a role is an Organization user (team member of Pro account)
 */
export function isOrganizationRole(role: string | Roles): boolean {
  return [Roles.ADMIN_PRO, Roles.MEMBER_PRO, Roles.ACCOUNTANT_PRO].includes(role as Roles);
}

/**
 * Get display name for a role
 */
export function getRoleDisplayName(role: string | Roles): string {
  const displayNames: Record<Roles, string> = {
    [Roles.UPERCUT]: 'Upercut Admin',
    [Roles.PRO]: 'Pro User',
    [Roles.COMPANY]: 'Company',
    [Roles.FREELANCE]: 'Freelance',
    [Roles.TALENT]: 'Talent',
    [Roles.ADMIN_PRO]: 'Pro Admin',
    [Roles.MEMBER_PRO]: 'Pro Member',
    [Roles.ACCOUNTANT_PRO]: 'Pro Accountant',
  };
  
  return displayNames[role as Roles] || role;
}

/**
 * Check if a role has CMS access
 * Currently: Upercut and Pro users can access CMS
 */
export function hasCmsAccess(role: string | Roles): boolean {
  return isUpercutRole(role) || isProRole(role);
}

/**
 * Check if a role requires proId parameter for operations
 * Upercut users select proId, Pro users use their own ID
 */
export function requiresProIdSelection(role: string | Roles): boolean {
  return isUpercutRole(role);
}

/**
 * Check if a field should be visible based on user role
 * T084: Role-based form field rendering utility
 * 
 * @param role - User's role
 * @param fieldVisibility - Configuration object defining which roles can see the field
 * @returns boolean - Whether the field should be visible
 * 
 * @example
 * ```tsx
 * const shouldShowProIdField = shouldShowField(user.role, {
 *   onlyFor: [Roles.UPERCUT],
 *   hideFor: [Roles.PRO, Roles.COMPANY, Roles.FREELANCE]
 * });
 * ```
 */
export function shouldShowField(
  role: string | Roles,
  fieldVisibility: {
    onlyFor?: Roles[],
    hideFor?: Roles[],
  }
): boolean {
  const { onlyFor, hideFor } = fieldVisibility;
  
  // If onlyFor is specified, only show for those roles
  if (onlyFor && onlyFor.length > 0) {
    return onlyFor.includes(role as Roles);
  }
  
  // If hideFor is specified, hide for those roles
  if (hideFor && hideFor.length > 0) {
    return !hideFor.includes(role as Roles);
  }
  
  // Default: show the field
  return true;
}

/**
 * Get field visibility configuration for proId field
 * T083: ProId field should only be visible to Upercut users
 * 
 * @returns Field visibility configuration
 */
export function getProIdFieldVisibility() {
  return {
    onlyFor: [Roles.UPERCUT],
    hideFor: [Roles.PRO, Roles.COMPANY, Roles.FREELANCE],
  };
}

/**
 * Validate whether a user can set proId parameter
 * T086: Client-side validation preventing Pro users from setting proId
 * 
 * @param role - User's role
 * @param proId - The proId value being set
 * @param userId - The authenticated user's ID
 * @returns object - Validation result with isValid and error message
 * 
 * @example
 * ```tsx
 * const validation = validateProIdAccess(user.role, formData.proId, user.id);
 * if (!validation.isValid) {
 *   showError(validation.error);
 * }
 * ```
 */
export function validateProIdAccess(
  role: string | Roles,
  proId: string | null | undefined,
  userId: string
): { isValid: boolean; error?: string } {
  // Upercut users can set any proId
  if (isUpercutRole(role)) {
    return { isValid: true };
  }
  
  // Pro users should not have proId field at all
  // If proId is provided and doesn't match userId, it's invalid
  if (isProRole(role)) {
    if (!proId) {
      // No proId provided = valid (will use userId)
      return { isValid: true };
    }
    
    if (proId !== userId) {
      return {
        isValid: false,
        error: 'You are not allowed to access data for another Pro user',
      };
    }
    
    return { isValid: true };
  }
  
  // Other roles shouldn't have proId field
  if (proId) {
    return {
      isValid: false,
      error: 'Your account type is not authorized to access Pro data',
    };
  }
  
  return { isValid: true };
}

/**
 * Get the effective proId for an operation
 * Upercut users can specify proId, Pro users always use their own ID
 * 
 * @param role - User's role
 * @param userId - Authenticated user's ID
 * @param requestedProId - ProId from form/request (optional)
 * @returns effective proId to use
 */
export function getEffectiveProId(
  role: string | Roles,
  userId: string,
  requestedProId?: string | null
): string {
  // Upercut can use any proId
  if (isUpercutRole(role)) {
    return requestedProId || userId;
  }
  
  // Pro users always use their own ID, ignore requestedProId
  if (isProRole(role)) {
    return userId;
  }
  
  // Other roles: return userId or null based on context
  return userId;
}
